import React from "react";
import {
  Box,
  Typography,
  Avatar,
  AvatarGroup,
  Button,
  Stack,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import {
  HourglassEmpty as HourglassEmptyIcon,
  CheckCircle as CheckCircleIcon,
  PersonAdd as PersonAddIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import type { PublicCircle } from "components/Slices/circleApiSlice";
import type { RequestState } from "../types";
import { getCadence } from "lib/cadence";
import { formatCurrency } from "../utils";

interface PublicCircleCardProps {
  circle: PublicCircle;
  requestState: RequestState;
  onRequestJoin: (circleId: string) => void;
  onViewDetails: (circleId: string) => void;
  isRequesting: boolean;
  isUserActive?: boolean;
}

const getButtonContent = (requestState: RequestState, isRequesting: boolean) => {
  switch (requestState) {
    case "sending":
      return { text: "Requesting...", disabled: true, icon: <HourglassEmptyIcon fontSize="small" />, color: "primary" as const, variant: "outlined" as const };
    case "pending":
      return { text: "Awaiting Approval", disabled: true, icon: <CheckCircleIcon fontSize="small" />, color: "primary" as const, variant: "outlined" as const };
    case "sent":
      return { text: "Request Sent", disabled: true, icon: <CheckCircleIcon fontSize="small" />, color: "success" as const, variant: "outlined" as const };
    case "error":
      return { text: "Try Again", disabled: false, icon: <PersonAddIcon fontSize="small" />, color: "error" as const, variant: "contained" as const };
    default:
      return { text: "Request to Join", disabled: isRequesting, icon: <PersonAddIcon fontSize="small" />, color: "primary" as const, variant: "contained" as const };
  }
};

export const PublicCircleCard: React.FC<PublicCircleCardProps> = ({
  circle,
  requestState,
  onRequestJoin,
  onViewDetails,
  isRequesting,
  isUserActive = true,
}) => {
  const theme = useTheme();
  const buttonProps = getButtonContent(requestState, isRequesting);
  const isJoinDisabled = !isUserActive || buttonProps.disabled;

  const payoutMin = formatCurrency(circle.monthlyContribution * (circle.minMembers - 1), circle.currency);
  const payoutMax = formatCurrency(circle.monthlyContribution * (circle.maxMembers - 1), circle.currency);
  const currentPool = formatCurrency(circle.monthlyContribution * circle.memberCount, circle.currency);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
        bgcolor: "background.paper",
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        transition: "box-shadow 0.25s, transform 0.25s",
        overflow: "hidden",
        "&:hover": {
          boxShadow: "0 8px 32px rgba(0,0,0,0.13)",
          transform: "translateY(-3px)",
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "text.primary", lineHeight: 1.3, maxWidth: "72%" }} noWrap>
            {circle.title}
          </Typography>
          <Box sx={{ px: 1, py: 0.3, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.08), border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}` }}>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "primary.main", letterSpacing: "0.03em" }}>
              {circle.memberCount}/{circle.maxMembers} members
            </Typography>
          </Box>
        </Box>

        {circle.description && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.82rem", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {circle.description}
          </Typography>
        )}
      </Box>

      {/* Hero contribution */}
      <Box sx={{ px: 3, pb: 2 }}>
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.1em", mb: 0.5 }}>
          {getCadence(circle.intervalUnit, circle.intervalCount).adjective} Contribution
        </Typography>
        <Typography sx={{ fontSize: "1.75rem", fontWeight: 700, color: "primary.main", letterSpacing: "-0.02em", lineHeight: 1 }}>
          {formatCurrency(circle.monthlyContribution, circle.currency)}
          <Typography component="span" sx={{ fontSize: "0.85rem", fontWeight: 400, color: "text.secondary", ml: 0.5 }}>{getCadence(circle.intervalUnit, circle.intervalCount).suffix}</Typography>
        </Typography>
      </Box>

      <Divider sx={{ mx: 3 }} />

      {/* Stats row */}
      <Box sx={{ px: 3, py: 2, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.1em", mb: 0.4 }}>
            Next Payout
          </Typography>
          <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: "text.primary" }}>
            {dayjs(circle.nextPayout).format("MMM D, YYYY")}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.1em", mb: 0.4 }}>
            Payout Range
          </Typography>
          <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: "success.main" }}>
            {payoutMin} – {payoutMax}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mx: 3 }} />

      {/* Members + pool */}
      <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AvatarGroup max={4} sx={{ "& .MuiAvatar-root": { width: 28, height: 28, fontSize: "0.7rem", border: `2px solid ${theme.palette.background.paper}`, boxShadow: "0 0 0 1px rgba(0,0,0,0.08)" } }}>
            {circle.memberImages.map((img, i) => (
              <Avatar key={i} src={img} />
            ))}
          </AvatarGroup>
          <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
            {circle.memberCount} member{circle.memberCount !== 1 ? "s" : ""}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.08em" }}>Pool</Typography>
          <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "primary.main" }}>{currentPool}</Typography>
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ px: 3, pb: 3, mt: "auto" }}>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon fontSize="small" />}
            onClick={() => onViewDetails(circle.id)}
            sx={{ flex: 1, fontWeight: 600, fontSize: "0.82rem", borderColor: "divider", color: "text.secondary", "&:hover": { borderColor: "text.primary", color: "text.primary", bgcolor: "transparent" } }}
          >
            View Details
          </Button>
          <Button
            size="small"
            variant={!isUserActive ? "outlined" : buttonProps.variant}
            color={!isUserActive ? "inherit" : buttonProps.color}
            disabled={isJoinDisabled}
            startIcon={!isUserActive ? <LockIcon fontSize="small" /> : buttonProps.icon}
            onClick={() => onRequestJoin(circle.id)}
            sx={{ flex: 1, fontWeight: 600, fontSize: "0.82rem", ...(!isUserActive && { opacity: 0.6 }) }}
          >
            {!isUserActive ? "Complete Setup" : buttonProps.text}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
